/**
 * EmployeeDashboard — hub for the staff-facing tools.
 * Three cards: Account Management / Messages / Tasks.
 *
 * Replaces the standalone "المراسلات" entry that used to live on the main
 * dashboard welcome bar — now everything staff-related sits under here.
 */
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, UserCog, ListChecks, PenLine } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const CARDS = [
    {
      id: 'account',
      icon: UserCog,
      color: '#d4af37',
      route: '/account-settings?tab=profile',
      titleAr: 'إدارة الحساب',
      titleEn: 'Account Management',
      descAr: 'الملف الشخصي، كلمة المرور، الإشعارات والتفضيلات',
      descEn: 'Profile, password, notifications and preferences',
      testid: 'employee-dash-account',
    },
    {
      id: 'messages',
      icon: Mail,
      color: '#3b82f6',
      route: '/messages',
      titleAr: 'المراسلات',
      titleEn: 'Messages',
      descAr: 'الوارد، المرسل، المميز والمؤرشف',
      descEn: 'Inbox, sent, starred and archived',
      testid: 'employee-dash-messages',
    },
    {
      id: 'tasks',
      icon: ListChecks,
      color: '#10b981',
      route: '/tasks-hub',
      titleAr: 'المهام',
      titleEn: 'Tasks',
      descAr: 'إدراج مهمة جديدة للفريق واستقبال المهام الموكلة من الأدمن',
      descEn: 'Create internal tasks and receive tasks delegated by admin',
      testid: 'employee-dash-tasks',
    },
    {
      id: 'signatures',
      icon: PenLine,
      color: '#8B5CF6',
      route: '/signatures-approvals',
      titleAr: 'التواقيع والاعتماد',
      titleEn: 'Signatures & Approvals',
      descAr: 'إدارة التواقيع واعتماد المستندات',
      descEn: 'Manage signatures and document approvals',
      testid: 'employee-dash-signatures',
    },
  ];

  return (
    <DashboardLayout
      title="لوحة تحكم الموظف والمشرف"
      titleEn="Employee & Supervisor Dashboard"
      subtitle="إدارة الحساب، المراسلات، المهام والتواقيع في مكان واحد"
      subtitleEn="Manage account, messages, tasks and signatures in one place"
      screenCode="EMP-D01"
      backPath="/dashboard"
      onBack={() => navigate('/dashboard')}
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف والمشرف', labelEn: 'Employee & Supervisor Dashboard' },
      ]}
    >
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        data-testid="employee-dashboard"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.button
                key={card.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -5 }}
                onClick={() => navigate(card.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-start transition-all hover:border-[#d4af37]/40 hover:bg-white/[0.04]"
                data-testid={card.testid}
              >
                <div
                  className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center border transition-transform group-hover:scale-110"
                  style={{
                    backgroundColor: `${card.color}15`,
                    borderColor: `${card.color}40`,
                  }}
                >
                  <Icon className="h-7 w-7" style={{ color: card.color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-1">
                  {isAr ? card.titleAr : card.titleEn}
                </h3>
                <p className="text-sm text-[#71717A] leading-relaxed">
                  {isAr ? card.descAr : card.descEn}
                </p>
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
